import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography, Fade, Modal } from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

export interface LightboxImage {
  src: string;
  alt?: string;
  caption?: string;
  subcaption?: string;
}

interface ImageLightboxProps {
  open: boolean;
  images: LightboxImage[];
  initialIndex?: number;
  onClose: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ open, images, initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, images.length - 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, images.length]);

  if (!images.length) return null;
  const current = images[Math.min(index, images.length - 1)];
  const hasMultiple = images.length > 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(10, 10, 10, 0.92)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Fade in={open} timeout={300}>
        <Box
          sx={{
            position: 'relative',
            outline: 'none',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)', transform: 'none' },
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {hasMultiple && (
            <IconButton
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.08)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)', transform: 'translateY(-50%)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
          )}

          {hasMultiple && (
            <IconButton
              onClick={() => setIndex((i) => Math.min(images.length - 1, i + 1))}
              disabled={index === images.length - 1}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#fff',
                backgroundColor: 'rgba(255,255,255,0.08)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)', transform: 'translateY(-50%)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: '92vw',
              maxHeight: '92vh',
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={current.src}
              alt={current.alt || current.caption || ''}
              sx={{
                maxWidth: '92vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                animation: 'fadeInScale 0.35s ease-out',
              }}
            />
            {(current.caption || current.subcaption) && (
              <Box sx={{ textAlign: 'center', color: '#fff' }}>
                {current.caption && (
                  <Typography variant="h6" sx={{ fontFamily: 'Playfair Display, serif' }}>
                    {current.caption}
                  </Typography>
                )}
                {current.subcaption && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {current.subcaption}
                  </Typography>
                )}
              </Box>
            )}
            {hasMultiple && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                {index + 1} / {images.length} · use ← → to navigate
              </Typography>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default ImageLightbox;
